import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, AlertTriangle, Filter, ArrowUpDown, RefreshCw, Check } from 'lucide-react';
import { Card } from '../../shared/Card';
import { useListingsQueue, COLLECTIONS, type SortOption } from '../../../hooks/useListingsQueue';
import { getRelativeTime } from '../../../lib/utils';
import { Spinner } from '../../ui/spinner';

interface FilterState {
  minPrice?: number;
  maxPrice?: number;
  selectedCollections: string[];
}

export function SalesActivity() {
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<SortOption>('time_desc');
  const [filters, setFilters] = useState<FilterState>({
    selectedCollections: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCollectionSelect, setShowCollectionSelect] = useState(false);
  
  const { 
    listings, 
    failedCollections, 
    isLoading, 
    error, 
    currentCollection,
    refetch,
    getListingImageUrl 
  } = useListingsQueue(
    selectedCollections.length > 0 ? selectedCollections : undefined,
    sortOption,
    filters
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (error) {
      console.error('Error refreshing listings:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(event.target.value as SortOption);
  };

  const handleCollectionToggle = (address: string) => {
    setSelectedCollections(prev => {
      if (prev.includes(address)) {
        return prev.filter(a => a !== address);
      } else {
        return [...prev, address];
      }
    });
  };

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Card className="flex flex-col h-[calc(100vh-8rem)] bg-gradient-to-br from-[#0054fa]/10 via-[#002787]/10 to-[#030a21]/80 backdrop-blur-xl border border-blue-500/20">
      <Card.Header className="flex-shrink-0 border-b border-blue-500/20 pb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
            Active Listings
          </h2>
          <motion.button
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            className="p-2 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg text-blue-400 transition-colors disabled:opacity-50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <RefreshCw 
              className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} 
            />
          </motion.button>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </button>
          
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-3 overflow-hidden"
              >
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-blue-400 mb-1">Min Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={filters.minPrice || ''}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value ? Number(e.target.value) : undefined)}
                      className="w-full bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2 text-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="Min APE"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-blue-400 mb-1">Max Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={filters.maxPrice || ''}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value ? Number(e.target.value) : undefined)}
                      className="w-full bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2 text-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="Max APE"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="relative">
            <button
              onClick={() => setShowCollectionSelect(!showCollectionSelect)}
              className="w-full bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2 text-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-left flex justify-between items-center"
            >
              <span>
                {selectedCollections.length === 0 
                  ? "All Collections" 
                  : `${selectedCollections.length} Selected`}
              </span>
              <ArrowUpDown className="w-4 h-4 text-blue-400" />
            </button>

            <AnimatePresence>
              {showCollectionSelect && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute z-50 w-full mt-2 bg-slate-900/95 border border-blue-500/20 rounded-lg shadow-lg backdrop-blur-sm max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-500/20 scrollbar-track-transparent"
                >
                  {COLLECTIONS.map(collection => (
                    <div
                      key={collection.address}
                      className="flex items-center gap-2 p-2 hover:bg-blue-500/10 cursor-pointer"
                      onClick={() => handleCollectionToggle(collection.address)}
                    >
                      <div className="w-4 h-4 border border-blue-500/50 rounded flex items-center justify-center">
                        {selectedCollections.includes(collection.address) && (
                          <Check className="w-3 h-3 text-blue-400" />
                        )}
                      </div>
                      <img
                        src={collection.image}
                        alt={collection.name}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-sm text-blue-100">{collection.name}</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-blue-400" />
            <div className="relative flex-1">
              <select
                value={sortOption}
                onChange={handleSortChange}
                className="w-full bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2 text-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none cursor-pointer"
              >
                <option value="time_desc" className="bg-slate-900">Latest First</option>
                <option value="time_asc" className="bg-slate-900">Oldest First</option>
                <option value="price_asc" className="bg-slate-900">Price: Low to High</option>
                <option value="price_desc" className="bg-slate-900">Price: High to Low</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <Spinner variant="bars" className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">
              {selectedCollections.length === 0 ? "Checking all collections" : `Checking ${selectedCollections.length} collections`}
            </span>
          </div>
          <span className="text-sm font-medium text-blue-400">
            {listings.length} listings
          </span>
        </div>
      </Card.Header>

      <Card.Content className="flex-grow overflow-y-scroll custom-scrollbar scrollbar-thin scrollbar-thumb-blue-500/80 scrollbar-track-blue-500/20 hover:scrollbar-thumb-blue-500/90 max-h-[700px]">
        <AnimatePresence>
          {failedCollections.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mb-4"
            >
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-yellow-400 text-sm">
                      Some collections failed to load: {failedCollections.join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-2">
          {isLoading && listings.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Spinner variant="bars" className="w-12 h-12 text-blue-400" />
            </div>
          ) : error && listings.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 max-w-md">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-medium">Error Loading Listings</p>
                    <p className="text-red-400/80 text-sm mt-1">
                      {error instanceof Error ? error.message : 'Failed to fetch listings data'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : listings.length > 0 ? (
            <AnimatePresence mode="popLayout">
              {listings.map((listing) => {
                const collection = COLLECTIONS.find(
                  c => c.address.toLowerCase() === listing.contract.toLowerCase()
                );

                const tokenId = listing.tokenSetId.split(':')[2];
                const imageUrl = getListingImageUrl(collection, tokenId);

                const marketplace = listing.source.domain.includes('magiceden') ? 'MagicEden' : 'OpenSea';
                const marketplaceColor = marketplace === 'MagicEden' ? 'text-purple-400' : 'text-blue-400';

                return (
                  <motion.a
                    key={listing.id}
                    href={listing.source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="block bg-gradient-to-r from-[#0054fa]/5 to-[#002787]/5 hover:from-[#0054fa]/10 hover:to-[#002787]/10 border border-blue-500/20 rounded-xl p-4 transition-all cursor-pointer group relative overflow-hidden min-h-16"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                      initial={{ x: '-100%' }}
                      whileHover={{ x: '100%' }}
                      transition={{ duration: 0.8, ease: 'easeInOut' }}
                    />
                    <div className="flex items-center gap-4 relative z-10">
                      <div className="relative w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={collection?.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-white text-base mb-1 truncate bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">
                              {collection?.name || 'Unknown Collection'}
                            </h3>
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-blue-300">#{tokenId}</p>
                              <span className={`text-xs ${marketplaceColor}`}>
                                • {marketplace}
                              </span>
                            </div>
                            <p className="text-xs text-green-400 flex items-center gap-1 mt-1">
                              <Clock className="w-3 h-3" />
                              {getRelativeTime(new Date(listing.createdAt).getTime())}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-white flex items-center gap-1 justify-end group-hover:text-blue-400 transition-colors">
                              {listing.price.amount.decimal} APE
                            </p>
                            <p className="text-xs text-blue-400">
                              ≈ ${listing.price.amount.usd.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.a>
                );
              })}
            </AnimatePresence>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-blue-400">No active listings found.</p>
            </div>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}